import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";

/**
 * MonthPicker — popover-style calendar that opens inline from a button.
 *
 * The trigger is "Mes" (Month) button. When clicked, a small panel opens
 * directly below it showing a 12-month grid for the current year (with
 * year navigation arrows). Clicking a month immediately selects it and
 * closes the popover. The current month (when no override) is highlighted.
 *
 * Props:
 *   value      string | "" — "YYYY-MM" or "" for "current month"
 *   onChange   fn(value)
 *   active     bool — whether this trigger is currently the selected period
 *   label      string — button label (translated)
 *   testid     string
 */
export default function MonthPicker({ value, onChange, active, label = "Mes", testid = "month-picker" }) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => {
    if (value) return parseInt(value.slice(0, 4), 10);
    return new Date().getFullYear();
  });
  const ref = useRef(null);

  // Sync year when external value changes
  useEffect(() => {
    if (value) setYear(parseInt(value.slice(0, 4), 10));
  }, [value]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const months = lang === "en"
    ? ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    : ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const currentValueKey = value || todayKey;

  // Trigger label: show "Mes" or the selected month label
  const triggerLabel = (() => {
    if (!value) return label;
    const [y, m] = value.split("-");
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  })();

  return (
    <div className="relative inline-block" ref={ref} data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-trigger`}
        className={`px-3 py-1.5 text-xs font-medium border-r border-slate-200 dark:border-slate-700 last:border-r-0 transition-colors flex items-center gap-1.5 ${
          active
            ? "bg-slate-900 text-white"
            : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
        }`}
      >
        {triggerLabel}
        {value && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
            className="opacity-70 hover:opacity-100 -mr-1"
            data-testid={`${testid}-clear`}
            title={lang === "en" ? "Back to current month" : "Volver al mes actual"}
          >
            <X size={11} weight="bold" />
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-full mt-1.5 left-0 z-40 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg rounded-md w-[260px] p-3"
          data-testid={`${testid}-panel`}
        >
          {/* Year navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setYear((y) => y - 1)}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
              data-testid="month-picker-year-prev"
            >
              <CaretLeft size={14} className="text-slate-500" />
            </button>
            <div className="text-sm font-semibold text-slate-900 dark:text-white font-mono">{year}</div>
            <button
              onClick={() => setYear((y) => Math.min(y + 1, today.getFullYear()))}
              disabled={year >= today.getFullYear()}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="month-picker-year-next"
            >
              <CaretRight size={14} className="text-slate-500" />
            </button>
          </div>

          {/* 12-month grid */}
          <div className="grid grid-cols-3 gap-1">
            {months.map((m, idx) => {
              const monthNum = idx + 1;
              const key = `${year}-${String(monthNum).padStart(2, "0")}`;
              const isFuture = year > today.getFullYear() || (year === today.getFullYear() && monthNum > today.getMonth() + 1);
              const isToday = key === todayKey;
              const isSelected = key === currentValueKey;
              return (
                <button
                  key={m}
                  onClick={() => {
                    if (isFuture) return;
                    onChange(isToday ? "" : key);
                    setOpen(false);
                  }}
                  disabled={isFuture}
                  data-testid={`month-${key}`}
                  className={`px-2 py-2 text-xs font-medium rounded transition-all relative ${
                    isFuture
                      ? "text-slate-300 dark:text-slate-700 cursor-not-allowed"
                      : isSelected
                        ? "bg-slate-900 text-white"
                        : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  {m}
                  {isToday && !isSelected && (
                    <span className="absolute top-1 right-1 w-1 h-1 bg-amber-400 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer: current shortcut */}
          {value && (
            <button
              onClick={() => { onChange(""); setOpen(false); }}
              className="mt-3 w-full text-[11px] text-slate-500 hover:text-slate-900 dark:hover:text-white py-1.5 border-t border-slate-100 dark:border-slate-800"
              data-testid="month-picker-current"
            >
              {lang === "en" ? "↻ Back to current month" : "↻ Volver al mes actual"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
